import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { ThemeName } from '../../logic/store/UI/UISlice';
import { useUrlTheme } from '../../logic/Utility/useUrlTheme';
import * as queryString from 'query-string';
import { IJdConnectFarmFieldOrgResponse, JdConnectFieldMap } from '../../logic/Map/JdConnectFieldMap/JdConnectFieldMap';
import { useGetJdConnectFields } from './useGetJdConnectFields';
import { useLoadingCounter } from '../../logic/Utility/useLoadingCounter';
import { Api } from '../../logic/Api/Api';
import { useScopedSession } from '../../tracing/session';
import { CoveringLoader } from '../../components/LoadingSpinner/CoveringLoader';
import { ScaleLoader } from 'react-spinners';
import styled, { useTheme } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { ReactComponent as CoreLogo } from '../../assets/images/GH-GHX-Logos-Orange.svg';
import { ReactComponent as EnogenCoreLogo } from '../../assets/images/EluminateEnogenLogos.svg';
import { Divider, Tree, Tooltip } from 'antd';
import { DataNode } from 'antd/lib/tree';
import { dynamicSort } from '../../logic/Utility/Utils';
import { usePostJdConnectFields } from './usePostJdConnectFields';
import { Button } from '../../components/Button/Button';
import { flex } from 'styled-system';

const MainContainer = styled.div`
	min-width: 382px;
	max-width: 382px;
	background-color: ${themeGet('colors.white')};
	display: flex;
	flex-direction: column;
`;

const StyledDivider = styled(Divider)`
	&.ant-divider {
		border-top-color: black;
		margin-top: 10px;
		margin-bottom: 10px;
	}
`;

const StyledTree = styled(Tree)`
	.ant-tree-checkbox-checked .ant-tree-checkbox-inner {
		background-color: ${themeGet('colors.primary')};
		border-color: ${themeGet('colors.primary')};
	}
	.ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner::after {
		background-color: ${themeGet('colors.primary')};
	}
`;

interface IFieldSelectProps
{
	changeTheme: (name: ThemeName) => void;
}

export const JdFieldSelect = (props: IFieldSelectProps) => 
{
	const {
		changeTheme,
	} = props;

	// Make sure our themeing is correct per the url
	const [isEnogen, setIsEnogen] = useState(false);
	useUrlTheme(setIsEnogen, changeTheme);
	const theme = useTheme();
	const session = useScopedSession('jdconnect-fields');
	const [api] = useState(new Api('api', null, session));

	// Grab the invitation from the url
	const parsedQueryString = queryString.parse(location.search);
	const invitationCode = (typeof (parsedQueryString.code) === 'string' && parsedQueryString.code.length) ? parsedQueryString.code : '';

	// When loading > 0 a blocking spinner will show
	const { isLoading, startLoading, endLoading } = useLoadingCounter();

	const [message, setMessage] = useState<ReactNode>();
	const [treeData, setTreeData] = useState<DataNode[]>([]);

	const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
	const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
	const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);
	const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);
	const [saveTooltipMessage, setSaveTooltipMessage] = useState<string>('');

	const jdConnectOrgTree: IJdConnectFarmFieldOrgResponse[] = useGetJdConnectFields(invitationCode,
		api,
		setMessage,
		startLoading,
		endLoading);
	
	useEffect(() =>
	{
		if (jdConnectOrgTree && jdConnectOrgTree.length)
		{
			const organizedTree = jdConnectOrgTree.sort(dynamicSort('Name'))
				.map(o =>
				{
					// Filter out any empty farms
					o.FarmList = o.FarmList.filter(fa => fa.Fields.length > 0).map(fa =>
					{
						fa.Fields.sort(dynamicSort('Name'));

						return fa;
					});

					o.FarmList.sort(dynamicSort('Name'));
					return o;
				});

			const data: DataNode[] = organizedTree.map(org =>
			{
				const orgNode: DataNode = {
					key: org.Key,
					title: <div style={{ fontWeight: theme.fontWeights.bold }}>{org.Name}</div>,
					children: org.FarmList.map(farm =>
					{
						return ({
							title: <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
								<div
									style={{
										height: 13,
										width: 13,
										borderRadius: '50%',
										backgroundColor: farm.Color,
										marginRight: 5,
										position:'relative',
										top: -1
									}}>{''}</div>
								{farm.Name}
							</div>,
							key: farm.Key, //concat the IDs to make a unique tree key
							children: farm.Fields.map(field =>
							{
								return ({
									title: field.Name,
									key: field.Key, //concat the IDs to make a unique tree key
								});
							})
						});
					}),
				};

				return orgNode;
			});

			setExpandedKeys([...data.map(o => o.key)]);
			setTreeData([...data]);
		}
	}, [jdConnectOrgTree]);

	const onExpand = (expandedKeysValue: React.Key[]) =>
	{
		// if not set autoExpandParent to false, if children expanded, parent can not collapse.
		// or, you can remove all expanded children keys.
		setExpandedKeys(expandedKeysValue);
		setAutoExpandParent(false);
	};

	const onCheck = (checkedKeysValue: React.Key[]) =>
	{
		setCheckedKeys(checkedKeysValue);
	};

	/**
	 * Called when the map selects a field or de-selects a field with a mouse click
	 * @param fieldId 
	 */
	const selectSingleField = (fieldKey: string) =>
	{
		let currentCheckedValues = checkedKeys as string[];
		const currentExpandedValues = expandedKeys as string[];
		const currentFields = jdConnectOrgTree.flatMap(o => o.FarmList.flatMap(fa => fa.Fields));
		const foundField = currentFields.find(f => f.Key === fieldKey);

		if (currentCheckedValues.findIndex(v => v === fieldKey) > -1)
		{
			currentCheckedValues = currentCheckedValues.filter(v => v !== fieldKey);
			// Check for the field's farm - remove it from the checked values list as it is now indeterminate
			if (currentCheckedValues.findIndex(v => v === foundField.FarmKey) > -1)
			{
				currentCheckedValues = currentCheckedValues.filter(v => v !== foundField.FarmKey);
			}
		}
		else
		{
			currentCheckedValues.push(fieldKey);
		}

		let expandedUpdated = false;
		if (currentExpandedValues.findIndex(v => v === foundField.FarmKey) === -1)
		{
			currentExpandedValues.push(foundField.FarmKey);
			expandedUpdated = true;
		}

		if (expandedUpdated)
		{
			setExpandedKeys([...currentExpandedValues]);
		}
		setCheckedKeys([...currentCheckedValues]);
	};

	const onSave = () =>
	{
		const saveRequest = jdConnectOrgTree.map(org =>
		{
			return {
				OrganizationId: org.Id,
				Farms: org.FarmList.map(farm =>
				{
					return {
						FarmId: farm.Id,
						Fields: farm.Fields.map(f =>
						{
							return {
								FieldId: f.Id,
								BoundaryId: f.BoundaryId,
								Authorized: (checkedKeys as string[]).includes(f.Key),
							};
						})
					};
				})
			};
		});

		// Send the request and it will then re-direct to the complete page
		usePostJdConnectFields(invitationCode, isEnogen, api, saveRequest, setMessage, startLoading, endLoading);
	};

	useEffect(() =>
	{
		if (!jdConnectOrgTree)
		{
			setIsSaveDisabled(true);
			setSaveTooltipMessage('At least one field should be selected.');
			return;
		}

		// If there are no orgs at all, disable the save button
		if (jdConnectOrgTree.length === 0)
		{
			setIsSaveDisabled(true);
			setSaveTooltipMessage('At least one organization with fields is required.');
			return;
		}

		// If there is a single org but it has no farms, which means no fields, disabled the save button
		if (jdConnectOrgTree.length === 1 && jdConnectOrgTree[0].FarmList.length === 0)
		{
			setIsSaveDisabled(true);
			setSaveTooltipMessage('At least one organization with fields is required.');
			return;
		}

		// If are no fields that are selected, disable the save button
		if (jdConnectOrgTree.length >= 1 && checkedKeys.length === 0)
		{
			setIsSaveDisabled(true);
			setSaveTooltipMessage('At least one field should be selected.');
			return;
		}

		setSaveTooltipMessage('');
		setIsSaveDisabled(false);
	},[jdConnectOrgTree, checkedKeys]);
	
	return <>
		<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			<div style={{ width: '100%', height: '100%', display: 'flex', flex: '1 1 auto', overflow: 'hidden' }}>
				<MainContainer>
					<div style={{width:'100%', display: 'flex', flexDirection:'column', alignItems: 'center', marginTop: 10, paddingRight: 5, paddingLeft: 5 }}>
						{
							isEnogen ?
								<EnogenCoreLogo  width={'100%'} height={79} />
								:
								<CoreLogo  width={'100%'} height={79} />
						}
					</div>
					<StyledDivider className='Divider' />
					{
						jdConnectOrgTree && !isLoading && 
						<div style={{ height: '100%', overflowY: 'auto' }}>
							<div style={{ fontWeight: theme.fontWeights.bold, paddingRight: 5, paddingLeft: 5 }}>
								Select one or more boundaries to share with your advisor. These will update periodically until you choose to disconnect the accounts. Once done, press 'Save.'
							</div>
							<StyledDivider className='Divider' />
							<StyledTree
								checkable
								selectable={false}
								onExpand={onExpand}
								expandedKeys={expandedKeys}
								autoExpandParent={autoExpandParent}
								onCheck={onCheck}
								onSelect={onCheck}
								checkedKeys={checkedKeys}
								treeData={treeData}
							/>
						</div>
					}
					{
						/** Show the error message from the api if there is one and not the save button */
						message ?
							<div style={{ paddingRight: 5, paddingLeft: 5 }}>{message}</div>
							:
							!isLoading && 
							<div style={{ display: 'flex', flexDirection: 'row' }}>
								<Tooltip title={saveTooltipMessage} placement='topRight'>
									<span style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
										<Button
											variant={'main'}
											onClick={() => onSave()}
											disabled={isSaveDisabled}
											style={{ width: 150, marginBottom: 5, marginLeft: 'auto', marginRight: 10 }}
										>
											Save
										</Button>
									</span>
								</Tooltip>
							</div>
					}
				</MainContainer>
				<JdConnectFieldMap
					currentSelectedFieldIds={checkedKeys as string[]}
					orgFarmFieldList={jdConnectOrgTree}
					selectSingleField={selectSingleField}
				/>
			</div>
		</div>
		<CoveringLoader style={{ zIndex:999 }} duration={10} className={isLoading ? 'loading' : ''}>
			<ScaleLoader color={theme.colors.primary} loading={true} />
		</CoveringLoader>
	</>;
};